import moment from 'moment';
import { RRule, type RRuleSet } from 'rrule';
import { isRecurringOccurrenceVisible } from './recurring-occurrence-filter';
import { floatingOccurrenceToInstant } from '@bt-common/utils/recurrence-calculations';

/**
 * Expand a recurring message's ruleset into the occurrences the calendar draws.
 *
 * Extracted out of `WhatsAppSelectors.processRecurringMessage` so the DISPLAY
 * path can be exercised in a unit test next to the WRITE path
 * (`RRuleRecurrenceHandler`). Those two must agree: every recurrence bug this
 * file has seen was a disagreement between what the calendar drew and what the
 * rule (and therefore the backend) actually emits, and it stayed invisible
 * because the display side could only be reached through a Redux selector that
 * drags in the whole app. The selector now delegates here, so a test that
 * expands a series is running the real thing rather than a re-implementation.
 */

/** Keep local wall-clock time stable for UTC-based recurring rules across DST boundaries. */
export function adjustOccurrenceForDST(date: Date, baseOffset: number | undefined): Date {
    if (baseOffset === undefined) return date;
    const occurrenceOffset = date.getTimezoneOffset();
    if (occurrenceOffset === baseOffset) return date;
    const offsetDiff = baseOffset - occurrenceOffset;
    return new Date(date.getTime() - offsetDiff * 60 * 1000);
}

export interface RecurrenceBase {
    /** The series start the rule itself carries; falls back to the doc's dueDate. */
    dtstart: Date;
    /** A TZID rule is already zone-aware, so the wall-clock correction must not be applied twice. */
    isTzidRule: boolean;
    baseOffset: number | undefined;
    excludedMinuteValues: Set<number>;
}

export function resolveRecurrenceBase(ruleSet: RRuleSet, dueDate: string | Date | null | undefined): RecurrenceBase {
    const firstRule = ruleSet.rrules()[0];
    const explicitDtStart = firstRule?.origOptions?.dtstart;
    // The dueDate field tracks the NEXT occurrence, not the series start, so it is
    // only a fallback: using it as DTSTART would reset a COUNT-based rule.
    const dtstart = explicitDtStart || moment.utc(dueDate).toDate();
    return {
        dtstart,
        isTzidRule: !!(firstRule?.origOptions?.tzid || firstRule?.options?.tzid),
        baseOffset: dtstart?.getTimezoneOffset(),
        excludedMinuteValues: new Set(ruleSet.exdates().map(ed => moment(ed).startOf('minute').valueOf())),
    };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Runtime-TZ-INDEPENDENT expansion of a MIGRATED (`DTSTART;TZID=<iana>`) rule for
 * the calendar (BE#648 Phase 2).
 *
 * rrule's OWN tzid expansion (`RRuleSet.between`) is browser-TZ SENSITIVE: it is
 * correct only when the process runs in UTC and is off by the process's own offset
 * in any other zone (verified: an IL 10:10 series expands to 07:10Z under TZ=UTC
 * but 10:10Z under TZ=Asia/Jerusalem and 04:10Z under TZ=America/Sao_Paulo). So a
 * customer on a non-UTC browser would SEE occurrences at instants the backend never
 * schedules. The shared engine avoids this by expanding the rule as FLOATING and
 * mapping each occurrence's wall-clock to the true instant via luxon; this mirrors
 * that, reusing bt-common's `floatingOccurrenceToInstant` so what the calendar
 * DRAWS is byte-identical to what `expandRecurrenceInstants` / `calculateNextDuedate`
 * SCHEDULE — whatever the browser's own zone.
 */
function expandTzidOccurrences(
    ruleSet: RRuleSet,
    tzid: string,
    base: RecurrenceBase,
    dueDate: string | Date | null | undefined,
    rangeStart: Date,
    rangeEnd: Date,
): Date[] {
    const firstRule = ruleSet.rrules()[0];
    // rrule stores a TZID dtstart's WALL-CLOCK in the Date's UTC fields, so the
    // parsed origOptions are already a valid FLOATING rule; floating expansion uses
    // UTC methods and is therefore independent of the process TZ.
    //
    // UNTIL is DROPPED from the floating rule: per RFC 5545 (and the shared engine's
    // `toFloatingRule`/`getUntilInstant`) a TZID rule's UNTIL is a TRUE UTC instant,
    // NOT a wall-clock bound. Applying it inside the floating expansion would clip an
    // hour early and HIDE the last send the backend actually makes. Instead it is kept
    // as a true instant and the mapped (true) occurrences are filtered against it below.
    const floatingRule = new RRule({ ...firstRule.origOptions, tzid: null, until: null });
    const untilMs = (firstRule.origOptions?.until ?? firstRule.options?.until)?.getTime();

    // Expand over a floating window padded by ±1 day (covers any zone offset plus a
    // DST shift) and trim precisely by TRUE instant below.
    const lowerFloat = new Date(rangeStart.getTime() - MS_PER_DAY);
    const upperFloat = new Date(rangeEnd.getTime() + MS_PER_DAY);

    // rrule returns a TZID rule's EXDATEs already in the FLOATING frame (wall-clock
    // in UTC fields), so map them through the SAME per-occurrence mapper as the
    // occurrences and compare like-with-like in TRUE-instant terms.
    const excludedMinuteValues = new Set(
        ruleSet.exdates().map(ed => moment(floatingOccurrenceToInstant(ed, tzid)).startOf('minute').valueOf()),
    );
    // `base.dtstart` for a TZID rule is the floating wall-clock; the "before series
    // start" check compares TRUE instants, so map it too.
    const trueDtstart = floatingOccurrenceToInstant(base.dtstart, tzid);

    return floatingRule.between(lowerFloat, upperFloat, true)
        .map(floating => floatingOccurrenceToInstant(floating, tzid))
        // Match `RRuleSet.between`'s default (endpoints exclusive) in TRUE-instant terms.
        .filter(instant => instant.getTime() > rangeStart.getTime() && instant.getTime() < rangeEnd.getTime())
        // UNTIL as a TRUE UTC instant, inclusive (RFC 5545) — matches the engine.
        .filter(instant => untilMs === undefined || instant.getTime() <= untilMs)
        .filter(instant => isRecurringOccurrenceVisible(instant, {
            dtstart: trueDtstart,
            dueDate: dueDate ?? null,
            excludedMinuteValues,
            // No browser-offset (`adjustOccurrenceForDST`) correction on the tz path:
            // the luxon mapping has already produced the true instant, and the stored
            // `dueDate` of a migrated series is likewise a true instant.
            displayedOccurrence: instant,
        }))
        .sort((a, b) => a.getTime() - b.getTime());
}

/**
 * The occurrences of `ruleSet` that render in [rangeStart, rangeEnd] for a
 * pending document whose `dueDate` is its next unsent occurrence.
 */
export function expandRecurringOccurrences(
    ruleSet: RRuleSet,
    dueDate: string | Date | null | undefined,
    rangeStart: Date,
    rangeEnd: Date,
): Date[] {
    const base = resolveRecurrenceBase(ruleSet, dueDate);

    // A migrated (TZID) rule is expanded runtime-TZ-independently so the calendar
    // draws exactly the instants the engine schedules; rrule's native tzid
    // expansion cannot be trusted off UTC (see expandTzidOccurrences).
    if (base.isTzidRule) {
        const firstRule = ruleSet.rrules()[0];
        const tzid = (firstRule?.origOptions?.tzid || firstRule?.options?.tzid) as string;
        return expandTzidOccurrences(ruleSet, tzid, base, dueDate, rangeStart, rangeEnd);
    }

    // The DST adjustment has to be computed BEFORE the filter, not after it: the
    // already-sent test compares against a `dueDate` that is itself stored
    // DST-adjusted, while the EXDATE and DTSTART tests belong in the rule's own
    // frame. Filtering first meant comparing a raw instant to an adjusted one, and
    // across an autumn fall-back that hid the next occurrence due to send.
    return ruleSet.between(rangeStart, rangeEnd)
        .map((date) => ({ ruleInstant: date, shown: adjustOccurrenceForDST(date, base.baseOffset) }))
        .filter(({ ruleInstant, shown }) => isRecurringOccurrenceVisible(ruleInstant, {
            dtstart: base.dtstart,
            dueDate: dueDate ?? null,
            excludedMinuteValues: base.excludedMinuteValues,
            displayedOccurrence: shown,
        }))
        .map(({ shown }) => shown);
}
